'use client';

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import PositionChart, { type PositionChartHandle, type PositionLevels } from './PositionChart';
import { toast } from 'sonner';
import { X, ArrowLeft, Upload, Loader2, Zap, PenLine, TrendingUp, TrendingDown, ChevronDown, Plus, Minus, AlertCircle, BarChart2, CheckCircle2, Share2,  } from 'lucide-react';
import { TradeCardData } from './TradeCard';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase/client';
import { track } from '@/lib/analytics';
import { useLang } from '@/lib/i18n/LanguageContext';

interface TradeFormData {
  ticker: string;
  entryPrice: string;
  stopLoss: string;
  takeProfit1: string;
  takeProfit2?: string;
  takeProfit3?: string;
  caption?: string;
  externalUrl?: string;
}

const markets = ['FOREX', 'CRYPTO', 'STOCKS', 'INDICES', 'COMMODITIES'];
const timeframes = ['M1', 'M5', 'M15', 'M30', 'H1', 'H4', 'D1'];

interface TradePostModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTradePosted?: (trade: TradeCardData) => void;
  /** "Back" button — defaults to closing; the feed passes one that returns to the page the user came from. */
  onBack?: () => void;
}

function calcRR(entry: string, sl: string, tp: string, direction: 'BUY' | 'SELL'): string {
  const e = parseFloat(entry);
  const s = parseFloat(sl);
  const t = parseFloat(tp);
  if (!e || !s || !t) return '';
  const risk = direction === 'BUY' ? e - s : s - e;
  const reward = direction === 'BUY' ? t - e : e - t;
  if (risk <= 0 || reward <= 0) return '';
  return (reward / risk).toFixed(2);
}

// Best-effort market guess from the ticker so MARKET auto-fills as you type (still manually changeable).
// Returns null when it can't tell, so the current selection is left alone.
function detectMarket(raw: string): string | null {
  const t = (raw || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  if (t.length < 3) return null;
  if (/^(XAU|XAG|XPT|XPD|USOIL|UKOIL|WTI|BRENT|NGAS|NATGAS|COPPER|GOLD|SILVER)/.test(t)) return 'COMMODITIES';
  if (/^(GER40|DE40|DE30|DAX|NAS100|NDX|US30|DJI|US500|SPX|SP500|US100|UK100|FTSE|JP225|NIKKEI|EU50|STOXX|AUS200|HK50|FRA40|ESP35|NDX100)/.test(t)) return 'INDICES';
  if (/(USDT|USDC|PERP)$/.test(t)) return 'CRYPTO';
  if (/^(BTC|ETH|SOL|XRP|ADA|DOGE|BNB|DOT|AVAX|MATIC|LINK|LTC|TRX|SHIB|PEPE|WIF|BONK|TRUMP|ORDI|SEI|SUI|APT|ARB|OP|INJ|TIA|NEAR|ATOM|FIL|ICP|RUNE|MTL|GRASS|SENT|TON|FET|RNDR)/.test(t)) return 'CRYPTO';
  const CUR = '(AUD|CAD|CHF|EUR|GBP|JPY|NZD|USD|SGD|NOK|SEK|ZAR|MXN|TRY|CNH|HKD|PLN|DKK)';
  if (new RegExp(`^${CUR}${CUR}$`).test(t)) return 'FOREX';
  if (/^[A-Z]{1,5}$/.test(t)) return 'STOCKS'; // short alpha ticker → equity
  return null;
}

/**
 * Validates that an uploaded image looks like a chart analysis image.
 * Checks image dimensions (charts are typically landscape/wide) and
 * rejects images that are clearly not charts (portrait photos, square social images).
 * Returns null if valid, or an error message string if invalid.
 */
async function validateChartImage(file: File): Promise<string | null> {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const { width, height } = img;
      const aspectRatio = width / height;

      // Charts are almost always landscape (wider than tall)
      // Reject portrait images (likely photos of people, coins, etc.)
      if (aspectRatio < 0.8) {
        resolve('Please upload a chart image. Portrait-orientation images are not allowed — charts should be landscape format.');
        return;
      }

      // Reject very small images (likely icons or thumbnails, not real charts)
      if (width < 300 || height < 150) {
        resolve('Image is too small to be a chart. Please upload a full-size chart screenshot (minimum 300×150px).');
        return;
      }

      resolve(null);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve('Could not read image. Please try a different file.');
    };
    img.src = url;
  });
}

export default function TradePostModal({ isOpen, onClose, onTradePosted, onBack }: TradePostModalProps) {
  const { t } = useLang();
  const { session } = useAuth();
  const [direction, setDirection] = useState<'BUY' | 'SELL'>('BUY');
  // SIGNAL = actionable trade (entry/SL/TP, settles, counts). ANALYSIS = chart + thesis only.
  const [ideaType, setIdeaType] = useState<'SIGNAL' | 'ANALYSIS'>('SIGNAL');
  const isAnalysis = ideaType === 'ANALYSIS';
  const [selectedMarket, setSelectedMarket] = useState('FOREX');
  const [marketAuto, setMarketAuto] = useState(true); // MARKET follows the ticker until changed by hand
  const [selectedTimeframe, setSelectedTimeframe] = useState('H4');
  const [orderType, setOrderType] = useState<'MARKET' | 'LIMIT' | 'STOP'>('MARKET');
  const [riskMode, setRiskMode] = useState<'risk' | 'lot'>('risk');
  const [riskValue, setRiskValue] = useState('1');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showTP2, setShowTP2] = useState(false);
  const [showTP3, setShowTP3] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [shareAsSignal, setShareAsSignal] = useState(false);
  const router = useRouter();
  const [imageValidating, setImageValidating] = useState(false);
  const [chartWarning, setChartWarning] = useState<string | null>(null);
  const [nonChartConfirmed, setNonChartConfirmed] = useState(false);
  const dropZoneRef = useRef<HTMLLabelElement>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<TradeFormData>();

  const watchEntry = watch('entryPrice', '');
  const watchSL = watch('stopLoss', '');
  const watchTP1 = watch('takeProfit1', '');
  const watchTicker = watch('ticker', '');
  const liveRR = calcRR(watchEntry, watchSL, watchTP1, direction);

  // Visual position chart (TradingView-style). The user drags entry/SL/TP on real candles; on publish
  // we screenshot the chart so the stored image is exactly what they drew. Levels stay two-way synced
  // with the number inputs below (drag ⇄ type). Prices formatted to a sensible precision per magnitude.
  const positionChartRef = useRef<PositionChartHandle>(null);
  const fmtLevel = (p: number) => (p >= 100 ? p.toFixed(2) : p >= 1 ? p.toFixed(4) : p.toFixed(5));
  const chartLevels = useMemo<PositionLevels | null>(() => {
    const e = parseFloat(watchEntry), s = parseFloat(watchSL), t = parseFloat(watchTP1);
    return Number.isFinite(e) && Number.isFinite(s) && Number.isFinite(t) ? { entry: e, sl: s, tp: t } : null;
  }, [watchEntry, watchSL, watchTP1]);
  const applyChartLevels = (l: PositionLevels) => {
    setValue('entryPrice', fmtLevel(l.entry), { shouldDirty: true });
    setValue('stopLoss', fmtLevel(l.sl), { shouldDirty: true });
    setValue('takeProfit1', fmtLevel(l.tp), { shouldDirty: true });
  };

  // MARKET follows the ticker (BTCUSDT→CRYPTO, EURUSD→FOREX, AAPL→STOCKS, XAUUSD→COMMODITIES,
  // GER40→INDICES) until the user picks a market by hand.
  useEffect(() => {
    if (!marketAuto) return;
    const m = detectMarket(watchTicker || '');
    if (m) setSelectedMarket(m);
  }, [watchTicker, marketAuto]);

  // Helper: red border style when field has error
  const errorBorderStyle = (hasError: boolean): React.CSSProperties =>
    hasError ? { borderColor: 'var(--bearish)', boxShadow: '0 0 0 1px var(--bearish)' } : {};

  // Paste image support (Ctrl+V)
  useEffect(() => {
    if (!isOpen) return;
    function handlePaste(e: ClipboardEvent) {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.startsWith('image/')) {
          const file = items[i].getAsFile();
          if (file) {
            processImageFile(file);
            break;
          }
        }
      }
    }
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [isOpen]);

  // Fresh form on every open + track funnel entry.
  // Without this reset, react-hook-form keeps the previous post's ticker/entry/caption and the
  // last image — which shipped a XAUUSD ticker on a BTCUSD chart+caption in production.
  useEffect(() => {
    if (isOpen) {
      reset(); // clears ticker, entry, SL, TP1-3, caption, externalUrl
      setMarketAuto(true); // MARKET follows the ticker again on a fresh post
      setImageFile(null);
      setImagePreview(null);
      setChartWarning(null);
      setNonChartConfirmed(false);
      setShowTP2(false);
      setShowTP3(false);
      track('idea_post_started');
    }
  }, [isOpen, reset]);

  async function compressImage(file: File): Promise<File> {
    return new Promise((resolve) => {
      const MAX_WIDTH = 1920;
      const QUALITY = 0.82;
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          let { width, height } = img;
          if (width > MAX_WIDTH) {
            height = Math.round((height * MAX_WIDTH) / width);
            width = MAX_WIDTH;
          }
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (!ctx) { resolve(file); return; }
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob(
            (blob) => {
              if (!blob) { resolve(file); return; }
              const compressed = new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' });
              resolve(compressed);
            },
            'image/jpeg',
            QUALITY
          );
        };
        img.onerror = () => resolve(file);
        img.src = e.target?.result as string;
      };
      reader.onerror = () => resolve(file);
      reader.readAsDataURL(file);
    });
  }

  async function processImageFile(file: File) {
    // Validate MIME type on client side
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type?.toLowerCase())) {
      toast.error(t('Unsupported file format — please use JPG, JPEG, PNG, or WebP'));
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t('Image exceeds maximum file size of 10MB'));
      return;
    }

    // Validate that the image looks like a chart
    setImageValidating(true);
    const validationError = await validateChartImage(file);
    setImageValidating(false);

    if (validationError) {
      // Show inline warning instead of blocking — user can confirm and proceed
      setChartWarning(t('Please upload a chart image with your trade idea. Stock photos and generic images are not allowed.'));
      setNonChartConfirmed(false);
    } else {
      setChartWarning(null);
      setNonChartConfirmed(false);
    }

    const compressed = await compressImage(file);
    setImageFile(compressed);
    const url = URL.createObjectURL(compressed);
    setImagePreview(url);
  }

  const handleImageChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    processImageFile(file);
  }, []);

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }

  function handleDragLeave(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      processImageFile(file);
    } else if (file) {
      toast.error(t('Please drop an image file'));
    }
  }

  async function uploadImage(file: File, accessToken: string): Promise<string> {
    // Always go through the server route — it is the single moderation gate (is-a-chart + is-appropriate
    // vision check). We deliberately do NOT upload straight from the browser to Supabase Storage, because
    // that would bypass moderation entirely.
    const formData = new FormData();
    formData.append('file', file);

    const res = await fetch('/api/trades/upload-image', {
      method: 'POST',
      body: formData,
      credentials: 'include',
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    let json: any = {};
    try { json = await res.json(); } catch { /* non-JSON error body */ }

    if (!res.ok) {
      // 422 = moderation rejection (NOT_A_CHART / INAPPROPRIATE). Surface the specific reason so the
      // user knows to swap the image, not that something broke.
      const err = new Error(json.error || 'Failed to upload image');
      (err as any).code = json.code;
      throw err;
    }

    if (!json.url) {
      throw new Error('Upload succeeded but no URL was returned');
    }

    return json.url;
  }

  async function onSubmit(data: TradeFormData) {
    // If there's a chart warning and user hasn't confirmed, block submission
    if (chartWarning && !nonChartConfirmed) {
      toast.error(t('Please confirm your image is a chart, or upload a proper chart screenshot.'));
      return;
    }
    setLoading(true);
    try {
      let activeSession = session;

      if (!activeSession) {
        const { data: { session: liveSession } } = await supabase.auth.getSession();
        activeSession = liveSession;
      }

      const accessToken = activeSession?.access_token;

      if (!accessToken) {
        toast.error(t('Please sign in to post a trade idea'));
        setLoading(false);
        return;
      }

      let imageUrl: string | null = null;

      // ANALYSIS: the user's uploaded image IS the content. SIGNAL: an uploaded/pasted screenshot is
      // optional — when present it wins; otherwise the chart is drawn from the levels (client
      // screenshot of the position chart, else the server's levels-only render).
      let fileToUpload: File | null = imageFile;

      // Shared chart window — identical framing for the screenshot (now) and the live chart (later):
      // Entry/SL/TP box ±12% (vertical) + ~80 bars before / 20 after the publish time (horizontal).
      const TF_MS_MAP: Record<string, number> = { M1: 60e3, M5: 300e3, M15: 900e3, M30: 1800e3, H1: 3600e3, H4: 14400e3, D1: 86400e3 };
      let chartWindow: { fromTs: number; toTs: number; priceMin: number; priceMax: number } | null = null;
      if (!isAnalysis && data.entryPrice && data.stopLoss && data.takeProfit1) {
        const lv = [
          parseFloat(data.entryPrice), parseFloat(data.stopLoss), parseFloat(data.takeProfit1),
          showTP2 && data.takeProfit2 ? parseFloat(data.takeProfit2) : undefined,
          showTP3 && data.takeProfit3 ? parseFloat(data.takeProfit3) : undefined,
        ].filter((n): n is number => typeof n === 'number' && Number.isFinite(n));
        const lo = Math.min(...lv), hi = Math.max(...lv);
        const padPx = (hi - lo) * 0.12 || Math.abs(hi) * 0.01 || 1;
        const barMs = TF_MS_MAP[selectedTimeframe] || 3600e3;
        const nowMs = Date.now();
        chartWindow = {
          fromTs: Math.floor((nowMs - 80 * barMs) / 1000),
          toTs: Math.floor((nowMs + 20 * barMs) / 1000),
          priceMin: lo - padPx,
          priceMax: hi + padPx,
        };
      }

      // ANALYSIS → upload the user's own image (moderated). SIGNAL → screenshot the visual position
      // chart the user drew and upload THAT, so the stored image is pixel-identical to what they saw.
      // If the instrument had no candles to draw on, leave imageUrl null and the server draws its
      // levels-only SETUP card fallback (buildAndStoreChartImage) instead.
      if (fileToUpload) {
        try {
          imageUrl = await uploadImage(fileToUpload, accessToken);
        } catch (uploadErr: any) {
          toast.error(t(uploadErr.message || 'Failed to upload image'));
          setLoading(false);
          return;
        }
      } else if (!isAnalysis && positionChartRef.current?.hasCandles()) {
        try {
          const blob = await positionChartRef.current.capture();
          if (blob) imageUrl = await uploadImage(new File([blob], 'signal-chart.png', { type: 'image/png' }), accessToken);
        } catch (capErr) {
          console.warn('[post] chart capture failed — falling back to server render:', capErr);
        }
      }

      const res = await fetch('/api/trades/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          ticker: data.ticker.toUpperCase(),
          direction,
          ideaType,
          market: selectedMarket,
          timeframe: selectedTimeframe,
          // Analyses carry no actionable levels.
          entryPrice: isAnalysis ? undefined : data.entryPrice,
          stopLoss: isAnalysis ? undefined : data.stopLoss,
          takeProfit1: isAnalysis ? undefined : data.takeProfit1,
          takeProfit2: !isAnalysis && showTP2 ? data.takeProfit2 : undefined,
          takeProfit3: !isAnalysis && showTP3 ? data.takeProfit3 : undefined,
          caption: data.caption || null,
          imageUrl,
          externalUrl: data.externalUrl || null,
          pendingReview: chartWarning && nonChartConfirmed ? true : undefined,
          orderType,
          ...(riskMode === 'lot'
            ? { lotSize: parseFloat(riskValue) || undefined }
            : { riskPct: (parseFloat(riskValue) || 0) / 100 || undefined }),
          ...(chartWindow ? { chartFromTs: chartWindow.fromTs, chartToTs: chartWindow.toTs, chartPriceMin: chartWindow.priceMin, chartPriceMax: chartWindow.priceMax } : {}),
        }),
        credentials: 'include',
      });

      const result = await res.json();

      if (!res.ok) {
        if (res.status === 401) {
          toast.error(t('Please sign in to post a trade idea'));
        } else {
          toast.error(result.error || t('Failed to post trade'));
        }
        return;
      }

      // Fire on success (not on click) — failed/cancelled posts must not count.
      track('idea_published', {
        market: selectedMarket,
        direction,
        timeframe: selectedTimeframe,
        order_type: orderType,
        has_chart_image: !!(imageFile || imagePreview),
        share_as_signal: shareAsSignal,
      });

      reset();
      setImagePreview(null);
      setImageFile(null);
      setShowTP2(false);
      setShowTP3(false);
      setDirection('BUY');
      setSelectedMarket('FOREX');
      setSelectedTimeframe('H4');
      setOrderType('MARKET');
      setRiskMode('risk');
      setRiskValue('1');
      setChartWarning(null);
      setNonChartConfirmed(false);
      onClose();

      toast.success(t('Trade idea posted!'), {
        description: `${data.ticker.toUpperCase()} ${direction} — ${t('live in the feed')}`,
      });

      if (onTradePosted && result.trade) {
        onTradePosted(result.trade);
      }

      // "Share as signal after publishing" → open the idea with the Share modal auto-opening.
      if (shareAsSignal && result.trade?.id) {
        setShareAsSignal(false);
        router.push(`/ideas/${result.trade.id}?share=1`);
      }

      // XP is awarded server-side by /api/trades/create; surface the toast from its response.
      if (result.xpAward) {
        window.dispatchEvent(new CustomEvent('xp-earned', { detail: result.xpAward }));
      }

      // Trigger AI analysis (fire and forget)
      if (result.trade?.id) {
        try {
          fetch('/api/trades/ai-analysis', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
            body: JSON.stringify({
              tradeId: result.trade.id,
              ticker: data.ticker.toUpperCase(),
              direction,
              entryPrice: data.entryPrice,
              stopLoss: data.stopLoss,
              takeProfit1: data.takeProfit1,
              takeProfit2: showTP2 ? data.takeProfit2 : undefined,
              caption: data.caption || '',
              market: selectedMarket,
              timeframe: selectedTimeframe,
            }),
            credentials: 'include',
          }).catch(() => {});
        } catch {}
      }
    } catch (err) {
      toast.error(t('Network error — please try again'));
    } finally {
      setLoading(false);
    }
  }

  function handleClose() {
    reset();
    setImagePreview(null);
    setImageFile(null);
    setShowTP2(false);
    setShowTP3(false);
    setIsDragOver(false);
    setChartWarning(null);
    setNonChartConfirmed(false);
    setOrderType('MARKET');
    setRiskMode('risk');
    setRiskValue('1');
    onClose();
  }

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[200] animate-fade-in"
      style={{ background: 'var(--bg-base)' }}
    >
      <div
        className="w-full h-full overflow-y-auto overflow-x-hidden animate-slide-up"
        style={{ background: 'var(--bg-card)' }}
      >
        {/* Modal header */}
        <div
          className="flex items-center justify-between px-5 py-4 border-b sticky top-0 z-10"
          style={{ background: 'var(--bg-card)', borderColor: 'var(--border)' }}
        >
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => { onBack?.(); handleClose(); }}
              className="flex items-center gap-1 px-2.5 py-1.5 mr-1 rounded-lg text-sm font-semibold transition-colors duration-100"
              style={{ color: 'var(--text-secondary)', background: 'var(--bg-sunken)', border: '1px solid var(--border-subtle)' }}
            >
              <ArrowLeft size={15} />
              {t('Back')}
            </button>
            <div
              className="w-7 h-7 rounded-lg flex items-center justify-center"
              style={{ background: 'var(--accent-muted)' }}
            >
              <Zap size={14} style={{ color: 'var(--accent)' }} />
            </div>
            <h2 className="font-bold text-base" style={{ color: 'var(--text-primary)' }}>
              {t('Post Trade Idea')}
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="p-1.5 rounded-lg transition-colors duration-100"
            style={{ color: 'var(--text-muted)' }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-sunken)';
              (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-secondary)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
              (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-muted)';
            }}
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-5 flex flex-col gap-4 w-full max-w-3xl mx-auto">
          {/* What are you posting? — SIGNAL (actionable, settles, counts) vs ANALYSIS (chart + thesis).
              Each mode is colour-coded to match its feed badge: indigo Signal / amber Analysis. */}
          <div>
            <p className="text-xs font-semibold mb-2 uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
              {t('What are you posting?')}
            </p>
            <div className="flex gap-2 p-1 rounded-xl" style={{ background: 'var(--bg-sunken)', border: '1px solid var(--border)' }}>
              {([
                { v: 'SIGNAL', label: 'Signal', hint: 'Entry · SL · TP — settles & counts', Icon: Zap, bg: 'var(--accent-muted)', text: 'var(--accent)' },
                { v: 'ANALYSIS', label: 'Analysis', hint: 'Chart + thesis — not counted', Icon: PenLine, bg: 'var(--win-muted)', text: 'var(--win)' },
              ] as const).map((opt) => {
                const active = ideaType === opt.v;
                const Icon = opt.Icon;
                return (
                  <button key={opt.v} type="button" onClick={() => setIdeaType(opt.v)}
                    className="flex-1 px-3 py-2.5 rounded-lg text-sm font-bold transition-all duration-100 text-center"
                    title={t(opt.hint)}
                    style={{
                      background: active ? opt.bg : 'transparent',
                      color: active ? opt.text : 'var(--text-secondary)',
                      border: active ? `1px solid ${opt.text}` : '1px solid transparent',
                    }}>
                    <span className="inline-flex items-center justify-center gap-1.5"><Icon size={14} />{t(opt.label)}</span>
                    <span className="block text-[10px] font-normal mt-0.5" style={{ color: active ? opt.text : 'var(--text-muted)' }}>{t(opt.hint)}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Section: Core Trade Info */}
          <div>
            <p className="text-xs font-semibold mb-3 uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
              {t('Trade Setup')}
            </p>

            {/* Instrument · direction · market · timeframe — one compact row so the chart gets the room */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--text-secondary)', letterSpacing: '0.04em' }}>
                  {t('TICKER')} <span style={{ color: 'var(--bearish)' }}>*</span>
                </label>
                <input
                  {...register('ticker', {
                    required: t('Ticker is required'),
                    maxLength: { value: 12, message: t('Max 12 chars') },
                    pattern: { value: /^[A-Za-z0-9./_-]+$/, message: t('Letters and numbers only') },
                  })}
                  type="text"
                  placeholder="BTCUSD, EURUSD…"
                  className="input-base w-full px-3 py-2.5 text-sm font-mono uppercase"
                  style={{ textTransform: 'uppercase', ...errorBorderStyle(!!errors.ticker) }}
                />
                {errors.ticker && (
                  <p className="flex items-center gap-1 text-xs mt-1" style={{ color: 'var(--bearish)' }}>
                    <AlertCircle size={11} />
                    {errors.ticker.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--text-secondary)', letterSpacing: '0.04em' }}>
                  {t('DIRECTION')} <span style={{ color: 'var(--bearish)' }}>*</span>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setDirection('BUY')}
                    className="flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-bold transition-all duration-150"
                    style={{
                      background: direction === 'BUY' ? 'var(--bullish-muted)' : 'var(--bg-sunken)',
                      color: direction === 'BUY' ? 'var(--bullish)' : 'var(--text-secondary)',
                      border: direction === 'BUY' ? '1px solid var(--bullish)' : '1px solid var(--border)',
                    }}
                  >
                    <TrendingUp size={13} />
                    BUY
                  </button>
                  <button
                    type="button"
                    onClick={() => setDirection('SELL')}
                    className="flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-bold transition-all duration-150"
                    style={{
                      background: direction === 'SELL' ? 'var(--bearish-muted)' : 'var(--bg-sunken)',
                      color: direction === 'SELL' ? 'var(--bearish)' : 'var(--text-secondary)',
                      border: direction === 'SELL' ? '1px solid var(--bearish)' : '1px solid var(--border)',
                    }}
                  >
                    <TrendingDown size={13} />
                    SELL
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--text-secondary)', letterSpacing: '0.04em' }}>
                  {t('MARKET')}
                </label>
                <div className="relative">
                  <select
                    value={selectedMarket}
                    onChange={(e) => { setSelectedMarket(e.target.value); setMarketAuto(false); }}
                    className="input-base w-full px-3 py-2.5 text-sm appearance-none pr-8"
                    style={{ cursor: 'pointer' }}
                  >
                    {markets.map((m) => (
                      <option key={m} value={m} style={{ background: 'var(--bg-sunken)' }}>{m}</option>
                    ))}
                  </select>
                  <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--text-muted)' }} />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--text-secondary)', letterSpacing: '0.04em' }}>
                  {t('TIMEFRAME')}
                </label>
                <div className="relative">
                  <select
                    value={selectedTimeframe}
                    onChange={(e) => setSelectedTimeframe(e.target.value)}
                    className="input-base w-full px-3 py-2.5 text-sm font-mono appearance-none pr-8"
                    style={{ cursor: 'pointer' }}
                  >
                    {timeframes.map((tf) => (
                      <option key={tf} value={tf} style={{ background: 'var(--bg-sunken)' }}>{tf}</option>
                    ))}
                  </select>
                  <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--text-muted)' }} />
                </div>
              </div>
            </div>
          </div>

          {/* Order & Price Levels — signals only (analyses carry no entry/SL/TP) */}
          {!isAnalysis && (<>
          {/* Visual position tool — drag entry/SL/TP on a real chart (TradingView-style). Full-bleed
              (breaks out of the centred form) so the chart spans the whole screen width and most of
              its height. Two-way synced with the number inputs below; screenshotted on publish. */}
          <div style={{ width: '100vw', marginLeft: 'calc(50% - 50vw)', display: 'flex', justifyContent: 'center' }}>
           {/* 56vw was fine on a laptop but ~220px on a phone — the chart was unusable there. */}
           <div style={{ width: 'min(96vw, 880px)', minWidth: 0, padding: '0 12px' }}>
            <p className="text-xs font-semibold mb-2 uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
              {t('Draw your position')}
            </p>
            <PositionChart
              ref={positionChartRef}
              market={selectedMarket}
              ticker={(watchTicker || '').toString()}
              timeframe={selectedTimeframe}
              direction={direction}
              levels={chartLevels}
              onLevels={applyChartLevels}
              onTimeframe={(tf) => setSelectedTimeframe(tf)}
              height="max(440px, calc(100vh - 300px))"
            />
            <p className="text-[11px] mt-1.5" style={{ color: 'var(--text-muted)' }}>
              {t('Drag the')} <span style={{ color: 'var(--bullish)' }}>{t('target')}</span>, <span style={{ color: 'var(--win)' }}>{t('entry')}</span> {t('and')} <span style={{ color: 'var(--bearish)' }}>{t('stop')}</span> {t('lines — or type exact values below.')}
            </p>
           </div>
          </div>

          {/* Section: Order type + sizing */}
          <div>
            <p className="text-xs font-semibold mb-3 uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
              {t('Order & Size')}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--text-secondary)', letterSpacing: '0.04em' }}>{t('ORDER TYPE')}</label>
                <div className="flex gap-1.5">
                  {(['MARKET', 'LIMIT', 'STOP'] as const).map((o) => (
                    <button key={o} type="button" onClick={() => setOrderType(o)}
                      className="flex-1 px-2 py-2 rounded-lg text-xs font-medium transition-all duration-100"
                      style={{ background: orderType === o ? 'var(--accent-muted)' : 'var(--bg-sunken)', color: orderType === o ? 'var(--accent)' : 'var(--text-secondary)', border: orderType === o ? '1px solid var(--accent)' : '1px solid var(--border)' }}>
                      {o.charAt(0) + o.slice(1).toLowerCase()}
                    </button>
                  ))}
                </div>
                <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                  {orderType === 'MARKET' ? t('Fills now at market price.') : t('Rests until price reaches your trigger.')}
                </p>
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--text-secondary)', letterSpacing: '0.04em' }}>{t('POSITION SIZE')}</label>
                <div className="flex gap-1.5">
                  <div className="flex rounded-lg overflow-hidden shrink-0" style={{ border: '1px solid var(--border)' }}>
                    {(['risk', 'lot'] as const).map((m) => (
                      <button key={m} type="button" onClick={() => setRiskMode(m)}
                        className="px-2.5 py-2 text-xs font-medium transition-all duration-100"
                        style={{ background: riskMode === m ? 'var(--accent-muted)' : 'var(--bg-sunken)', color: riskMode === m ? 'var(--accent)' : 'var(--text-secondary)' }}>
                        {m === 'risk' ? t('Risk %') : 'Lot'}
                      </button>
                    ))}
                  </div>
                  <input type="text" inputMode="decimal" value={riskValue}
                    onChange={(e) => setRiskValue(e.target.value.replace(/[^0-9.]/g, ''))}
                    placeholder={riskMode === 'risk' ? '1' : '0.10'}
                    className="input-base flex-1 min-w-0 px-3 py-2 text-sm font-mono" />
                </div>
                <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                  {riskMode === 'risk' ? t('% of demo balance risked at SL.') : t('Fixed position size (units).')}
                </p>
              </div>
            </div>
          </div>

          {/* Section: Price Levels */}
          <div>
            <p className="text-xs font-semibold mb-3 uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
              {t('Price Levels')}
            </p>

            <div className="grid grid-cols-3 gap-3 mb-3">
              <div>
                <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--win)', letterSpacing: '0.04em' }}>
                  {orderType === 'MARKET' ? 'ENTRY' : 'TRIGGER'} <span style={{ color: 'var(--bearish)' }}>*</span>
                </label>
                <input
                  {...register('entryPrice', {
                    required: isAnalysis ? false : t('Required'),
                    pattern: { value: /^\d+(\.\d+)?$/, message: t('Invalid price') },
                  })}
                  type="text"
                  inputMode="decimal"
                  placeholder="0.00"
                  className="input-base w-full px-3 py-2.5 text-sm font-mono"
                  style={errorBorderStyle(!!errors.entryPrice)}
                />
                {errors.entryPrice && (
                  <p className="text-xs mt-1" style={{ color: 'var(--bearish)' }}>{errors.entryPrice.message}</p>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--bearish)', letterSpacing: '0.04em' }}>
                  STOP LOSS <span style={{ color: 'var(--bearish)' }}>*</span>
                </label>
                <input
                  {...register('stopLoss', {
                    required: isAnalysis ? false : t('Required'),
                    pattern: { value: /^\d+(\.\d+)?$/, message: t('Invalid price') },
                  })}
                  type="text"
                  inputMode="decimal"
                  placeholder="0.00"
                  className="input-base w-full px-3 py-2.5 text-sm font-mono"
                  style={errorBorderStyle(!!errors.stopLoss)}
                />
                {errors.stopLoss && (
                  <p className="text-xs mt-1" style={{ color: 'var(--bearish)' }}>{errors.stopLoss.message}</p>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--bullish)', letterSpacing: '0.04em' }}>
                  TAKE PROFIT <span style={{ color: 'var(--bearish)' }}>*</span>
                </label>
                <input
                  {...register('takeProfit1', {
                    required: isAnalysis ? false : t('Required'),
                    pattern: { value: /^\d+(\.\d+)?$/, message: t('Invalid price') },
                  })}
                  type="text"
                  inputMode="decimal"
                  placeholder="0.00"
                  className="input-base w-full px-3 py-2.5 text-sm font-mono"
                  style={errorBorderStyle(!!errors.takeProfit1)}
                />
                {errors.takeProfit1 && (
                  <p className="text-xs mt-1" style={{ color: 'var(--bearish)' }}>{errors.takeProfit1.message}</p>
                )}
              </div>
            </div>

            {/* Live R:R */}
            {liveRR && (
              <div
                className="flex items-center gap-2 px-3 py-2 rounded-lg mb-3"
                style={{ background: parseFloat(liveRR) >= 2 ? 'var(--bullish-muted)' : 'var(--win-muted)', border: `1px solid ${parseFloat(liveRR) >= 2 ? 'var(--bullish)' : 'var(--win)'}` }}
              >
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{t('Risk/Reward:')}</span>
                <span className="text-sm font-bold font-mono" style={{ color: parseFloat(liveRR) >= 2 ? 'var(--bullish)' : 'var(--win)' }}>
                  1:{liveRR}
                </span>
                {parseFloat(liveRR) >= 2 && <span className="text-xs" style={{ color: 'var(--bullish)' }}>✓ {t('Good setup')}</span>}
              </div>
            )}

            {/* TP2 / TP3 */}
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => setShowTP2(!showTP2)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-100"
                style={{
                  background: showTP2 ? 'var(--bullish-muted)' : 'var(--bg-sunken)',
                  color: showTP2 ? 'var(--bullish)' : 'var(--text-secondary)',
                  border: showTP2 ? '1px solid var(--bullish)' : '1px solid var(--border)',
                }}
              >
                {showTP2 ? <Minus size={11} /> : <Plus size={11} />}
                TP2
              </button>
              {showTP2 && (
                <input
                  {...register('takeProfit2', { pattern: { value: /^\d+(\.\d+)?$/, message: t('Invalid') } })}
                  type="text"
                  inputMode="decimal"
                  placeholder={t('TP2 price')}
                  className="input-base px-3 py-1.5 text-sm font-mono"
                  style={{ width: 120 }}
                />
              )}
              <button
                type="button"
                onClick={() => setShowTP3(!showTP3)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-100"
                style={{
                  background: showTP3 ? 'var(--bullish-muted)' : 'var(--bg-sunken)',
                  color: showTP3 ? 'var(--bullish)' : 'var(--text-secondary)',
                  border: showTP3 ? '1px solid var(--bullish)' : '1px solid var(--border)',
                }}
              >
                {showTP3 ? <Minus size={11} /> : <Plus size={11} />}
                TP3
              </button>
              {showTP3 && (
                <input
                  {...register('takeProfit3', { pattern: { value: /^\d+(\.\d+)?$/, message: t('Invalid') } })}
                  type="text"
                  inputMode="decimal"
                  placeholder={t('TP3 price')}
                  className="input-base px-3 py-1.5 text-sm font-mono"
                  style={{ width: 120 }}
                />
              )}
            </div>
          </div>
          </>)}

          {/* Section: Chart Image — ANALYSIS: the image IS the content. SIGNAL: an uploaded/pasted
              screenshot is optional (owner ask: phone posters want to attach their platform's chart);
              without one the chart is drawn from the levels (red risk / blue reward boxes). */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold mb-1 uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                {isAnalysis ? t('Chart Image') : t('Chart screenshot (optional)')}
              </p>
              {!imageFile && !isAnalysis && (
                <div className="flex items-center gap-1.5 px-2 py-1 rounded-md" style={{ background: 'var(--accent-muted)', border: '1px solid var(--accent-muted)' }}>
                  <BarChart2 size={11} style={{ color: 'var(--accent)' }} />
                  <span className="text-xs" style={{ color: 'var(--accent)' }}>{isAnalysis ? t('Auto-chart will be generated') : t('Auto-chart from your levels')}</span>
                </div>
              )}
            </div>

            {/* Chart-only notice */}
            <div
              className="flex items-start gap-2 px-3 py-2.5 rounded-lg mb-3"
              style={{ background: 'var(--win-muted)', border: '1px solid var(--win-muted)' }}
            >
              <AlertCircle size={13} className="shrink-0 mt-0.5" style={{ color: 'var(--win)' }} />
              <p className="text-xs leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                <span className="font-semibold" style={{ color: 'var(--win)' }}>{t('Chart images only.')}</span>{' '}
                {isAnalysis
                  ? t('Upload a chart screenshot from your trading platform, a candlestick chart, or an annotated setup. Non-chart images will be rejected. If no image is uploaded, a chart is auto-generated from your trade levels.')
                  : t('Paste (Ctrl+V) or upload a screenshot from your platform. If you skip it, the chart is drawn from your entry, SL and TP — red = risk, blue = reward.')}
              </p>
            </div>

            {/* Chart warning (non-chart image detected) */}
            {chartWarning && (
              <div
                className="flex flex-col gap-2 px-3 py-3 rounded-lg mb-3"
                style={{ background: 'var(--bearish-muted)', border: '1px solid var(--bearish)' }}
              >
                <div className="flex items-start gap-2">
                  <AlertCircle size={13} className="shrink-0 mt-0.5" style={{ color: 'var(--bearish)' }} />
                  <p className="text-xs leading-relaxed" style={{ color: 'var(--bearish)' }}>
                    {chartWarning}
                  </p>
                </div>
                <label className="flex items-center gap-2 cursor-pointer mt-1">
                  <input
                    type="checkbox"
                    checked={nonChartConfirmed}
                    onChange={(e) => setNonChartConfirmed(e.target.checked)}
                    className="w-3.5 h-3.5 rounded"
                    style={{ accentColor: 'var(--win)' }}
                  />
                  <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                    {t('I confirm this is a chart image — my post may be flagged for review')}
                  </span>
                </label>
              </div>
            )}

            {imagePreview ? (
              <div className="relative rounded-xl overflow-hidden" style={{ border: '1px solid var(--border)' }}>
                <img
                  src={imagePreview}
                  alt={t('Chart preview')}
                  className="w-full object-cover"
                  style={{ maxHeight: 240 }}
                />
                <div className="absolute top-2 right-2 flex items-center gap-1.5">
                  <div className="flex items-center gap-1 px-2 py-1 rounded-md" style={{ background: 'var(--bullish)', border: '1px solid var(--bullish-muted)' }}>
                    <CheckCircle2 size={11} style={{ color: 'white' }} />
                    <span className="text-xs font-semibold" style={{ color: 'white' }}>{t('Chart ready')}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setImageFile(null); setImagePreview(null); }}
                    className="p-1.5 rounded-lg"
                    style={{ background: 'rgba(0,0,0,0.7)', color: 'white' }}
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>
            ) : (
              <label
                ref={dropZoneRef}
                htmlFor="chart-upload"
                className="flex flex-col items-center justify-center gap-2 rounded-xl cursor-pointer transition-all duration-150"
                style={{
                  border: `2px dashed ${isDragOver ? 'var(--accent)' : 'var(--border)'}`,
                  background: isDragOver ? 'var(--accent-muted)' : 'var(--bg-sunken)',
                  padding: '28px 16px',
                  minHeight: 120,
                }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                {imageValidating ? (
                  <>
                    <Loader2 size={24} className="animate-spin" style={{ color: 'var(--accent)' }} />
                    <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{t('Validating image…')}</p>
                  </>
                ) : (
                  <>
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'var(--accent-muted)', border: '1px solid var(--accent-muted)' }}>
                      <BarChart2 size={20} style={{ color: 'var(--accent)' }} />
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                        {t('Drop chart screenshot here')}
                      </p>
                      <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                        {t('Paste (Ctrl/Cmd+V), drop, or')} <span style={{ color: 'var(--accent)' }}>{t('browse files')}</span> · PNG, JPG, WebP · max 10MB
                      </p>
                      <p className="text-xs mt-1.5 font-medium" style={{ color: 'var(--text-muted)' }}>
                        {t('Candlestick charts · Annotated setups only')}
                      </p>
                    </div>
                  </>
                )}
                <input
                  id="chart-upload"
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  className="hidden"
                  onChange={handleImageChange}
                />
              </label>
            )}
          </div>

          {/* Section: Caption */}
          <div>
            <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
              {t('Analysis / Caption')}
            </label>
            <textarea
              {...register('caption', { maxLength: { value: 1000, message: t('Max 1000 characters') } })}
              placeholder={t('Describe your trade setup, key levels, and reasoning…')}
              rows={3}
              className="input-base w-full px-3 py-2.5 text-sm resize-none"
              style={{ lineHeight: '1.5' }}
            />
            {errors.caption && (
              <p className="text-xs mt-1" style={{ color: 'var(--bearish)' }}>{errors.caption.message}</p>
            )}
          </div>

          {/* Share as signal after publishing */}
          <div
            className="flex items-center justify-between p-3 rounded-xl cursor-pointer transition-all duration-150"
            style={{
              background: shareAsSignal ? 'var(--accent-muted)' : 'var(--bg-sunken)',
              border: `1px solid ${shareAsSignal ? 'var(--accent)' : 'var(--border)'}`,
            }}
            onClick={() => setShareAsSignal(!shareAsSignal)}
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                style={{ background: shareAsSignal ? 'var(--accent-muted)' : 'var(--bg-sunken)' }}>
                <Share2 size={15} style={{ color: shareAsSignal ? 'var(--accent)' : 'var(--text-muted)' }} />
              </div>
              <div>
                <p className="text-sm font-semibold" style={{ color: shareAsSignal ? 'var(--accent)' : 'var(--text-secondary)' }}>
                  {t('Share as signal after publishing')}
                </p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  {t('Open the share sheet with a branded card + your referral link')}
                </p>
              </div>
            </div>
            <div className="w-10 h-5 rounded-full transition-all duration-200 shrink-0"
              style={{ background: shareAsSignal ? 'var(--accent)' : 'var(--border)', position: 'relative' }}>
              <div className="absolute top-0.5 w-4 h-4 rounded-full transition-all duration-200"
                style={{ background: 'white', left: shareAsSignal ? '22px' : '2px', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading || imageValidating}
            className="btn-primary w-full py-3 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 size={16} className="animate-spin" />
                {t('Posting…')}
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <Zap size={15} />
                {t('Post Trade Idea')}
              </span>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
